import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  History,
  ListChecks,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { HiOutlineArrowLongLeft, HiOutlineArrowLongRight } from "react-icons/hi2";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import { isRTL } from "../../utils/rtl";
import { toast } from "../../utils/toast";
import CommonButton from "../../components/common/CommonButton";
import ReactSelector from "../../components/common/CommonReactSelector";
import InventoryGroupPicker from "../../components/InventoryManagement/InventoryGroupPicker";
import {
  bulkCreateOrUpdateInventoryItems,
  createOrUpdateInventoryItem,
} from "../../service/MeterInventoryManagement";
import {
  FormField,
  InlineSpinner,
  OptionCard,
  SectionCard,
  TextInput,
  allowedSaveModes,
  apiErrorMessage,
  apiSuccessMessage,
  firstAllowedSaveMode,
  firstArray,
  getInventoryPermissions,
  isSealType,
  itemTypeOptionsFor,
  parseSealRange,
  unwrap,
  useInventoryScope,
  useInventoryStores,
} from "../../components/InventoryManagement/inventoryFormShared";
import {
  MAX_VISIBLE_ROW_ERRORS,
  RejectedRowList,
  SummaryTile,
  UploadDropZone,
  UploadStatusBadge,
  defaultUploadModeFor,
  useInventoryUploadRun,
  useUploadModePermissions,
} from "../../components/InventoryManagement/inventoryUploadShared";
import {
  autoMatchColumns,
  detectSerialHeader,
  downloadSealTemplate,
  toBackendColumnMappings,
  uploadFieldsFor,
} from "../../components/InventoryManagement/inventoryUploadColumns";
import InventoryColumnMapModal from "../../components/InventoryManagement/InventoryColumnMapModal";
import InventoryUploadHistory from "../../components/InventoryManagement/InventoryUploadHistory";

// Preference order when defaulting the save mode to something the role may actually use.
const MODE_PREFERENCE = ["CREATE_OR_UPDATE", "CREATE_ONLY", "UPDATE_ONLY"];

const FOOTER_BUTTON_CLASS = "!h-10 !min-h-[40px] !max-h-10 rounded-lg !px-5";

// The metadata bag the backend accepts is open-ended; these are the keys the
// meter/modem/SIM sheets actually carry, so the form offers exactly them.
const METADATA_FIELDS = [
  { key: "meterMake", labelKey: "inventoryAddMeterMake" },
  { key: "meterType", labelKey: "inventoryAddMeterType" },
  { key: "meterComponents", labelKey: "inventoryAddComponents" },
  { key: "manufacturingYear", labelKey: "inventoryAddManufacturingYear" },
  { key: "batchNo", labelKey: "inventoryAddBatchNo" },
  { key: "simNo", labelKey: "inventoryAddSimNo" },
  { key: "iccid", labelKey: "inventoryAddIccid" },
];

// A bulk row stays deliberately narrower than the manual form: meter number plus the
// handful of metadata columns that differ per unit.
const BULK_METADATA_FIELDS = [
  "meterMake",
  "meterType",
  "manufacturingYear",
  "batchNo",
  "simNo",
  "iccid",
];

// Manufacturing year is picked from a list rather than typed, so only a real year can
// be entered -- newest first, back to the oldest year a meter in the field could have.
const OLDEST_MANUFACTURING_YEAR = 1980;
const manufacturingYearOptions = () => {
  const options = [];
  for (let year = new Date().getFullYear(); year >= OLDEST_MANUFACTURING_YEAR; year -= 1) {
    options.push({ value: String(year), label: String(year) });
  }
  return options;
};

const YearSelect = ({ value, onChange, options, placeholder, testId }) => (
  <ReactSelector
    options={options}
    value={options.find((option) => option.value === value) || null}
    onChange={(option) => onChange(option?.value ?? "")}
    placeholder={placeholder}
    isSearchable
    isClearable
    height="40px"
    borderRadius="8px"
    fontSize="14px"
    menuPortalTarget={document.body}
    menuPosition="fixed"
    dataTestId={testId}
  />
);

// A seal range has no serial or meter details -- only its batch is worth recording.
const SEAL_METADATA_FIELDS = ["batchNo"];

const SEAL_RANGE_FIELDS = [
  { key: "sealPrefix", labelKey: "inventorySealPrefix", placeholderKey: "inventorySealPrefixPlaceholder", required: false },
  { key: "sealFrom", labelKey: "inventorySealFrom", placeholderKey: "inventorySealFromPlaceholder", required: true },
  { key: "sealTo", labelKey: "inventorySealTo", placeholderKey: "inventorySealToPlaceholder", required: true },
];

const emptySealRange = () => ({ sealPrefix: "", sealFrom: "", sealTo: "" });

const hasSealInput = (range) =>
  SEAL_RANGE_FIELDS.some((field) => String(range?.[field.key] ?? "").trim() !== "");

const emptyBulkRow = () => ({
  identifier: "",
  itemType: "",
  ...emptySealRange(),
  metadata: BULK_METADATA_FIELDS.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
});

const emptyMetadata = () =>
  METADATA_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: "" }), {});

const compactMetadata = (metadata) =>
  Object.entries(metadata || {}).reduce((acc, [key, value]) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed !== "" && trimmed !== null && trimmed !== undefined) acc[key] = trimmed;
    return acc;
  }, {});

export const AddInventory = () => {
  const { theme, langMode } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const t = translations[langMode] || translations.en;
  const navigate = useNavigate();
  // `?tab=import&view=history` opens straight on the upload history (the old
  // /inventory-management/import page redirects here).
  const [searchParams] = useSearchParams();

  const { projectId, organizationId } = useInventoryScope();
  const { storeOptions, loading: storesLoading } = useInventoryStores(true, projectId, langMode);

  // A role with create access only may create, one with update access only may update,
  // and the combined mode needs both -- so the mode the screen starts on is the first
  // one this role is actually allowed to use.
  const inventoryPerm = useMemo(() => getInventoryPermissions(), []);
  const modePermissions = useMemo(() => allowedSaveModes(inventoryPerm), [inventoryPerm]);
  const defaultMode = useMemo(
    () => firstAllowedSaveMode(inventoryPerm, MODE_PREFERENCE),
    [inventoryPerm]
  );

  // The sheet upload allows fewer modes than the manual form (no create-only), so it
  // keeps its own mode next to the shared store/item-type destination.
  const uploadModePermissions = useUploadModePermissions();
  const [uploadMode, setUploadMode] = useState(() =>
    defaultUploadModeFor(uploadModePermissions)
  );
  // Bumped whenever a run is acknowledged or finishes, so the history view reloads.
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const refreshHistory = useCallback(() => setHistoryRefreshKey((key) => key + 1), []);
  const upload = useInventoryUploadRun({
    projectId,
    organizationId,
    onRunFinished: refreshHistory,
  });

  const [entryMode, setEntryMode] = useState(() =>
    searchParams.get("tab") === "import" ? "import" : "manual"
  );
  // The Excel Upload tab's own toggle: the upload form or the list of past uploads.
  const [importView, setImportView] = useState(() =>
    searchParams.get("view") === "history" ? "history" : "upload"
  );
  const [mapOpen, setMapOpen] = useState(false);
  // `{ fieldKey: sheetHeader }` from the column mapping, kept per picked file so
  // reopening the mapping for the same sheet starts where the operator left off.
  const [columnMappings, setColumnMappings] = useState({});
  const [saving, setSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Shared by all tabs — the bulk payload sends these once at the top level.
  const [storeId, setStoreId] = useState("");
  const [itemType, setItemType] = useState("NEW_METER");
  const [mode, setMode] = useState(defaultMode);
  const [note, setNote] = useState("");
  // What happens to a serial that already exists. UPDATE_DETAILS leaves custody and
  // location alone; RETURN_TO_STORE moves the meter back into the selected store.
  const [updateAction, setUpdateAction] = useState("UPDATE_DETAILS");
  // Group custody, shared by the manual and bulk tabs. The picker is driven by the
  // group name, but the API takes the id as `assignedTo`; together with the store it
  // makes the item ASSIGNED.
  const [groupName, setGroupName] = useState("");
  const [groupId, setGroupId] = useState(null);

  // Manual-only fields.
  const [identifier, setIdentifier] = useState("");
  const [conditionNote, setConditionNote] = useState("");
  const [metadata, setMetadata] = useState(emptyMetadata);
  // SEAL replaces the serial with a Prefix / From / To range.
  const [sealRange, setSealRange] = useState(emptySealRange);
  const isSeal = isSealType(itemType);
  const sealPreview = isSeal && hasSealInput(sealRange) ? parseSealRange(sealRange) : null;

  const [bulkRows, setBulkRows] = useState([emptyBulkRow()]);

  const itemTypeOptions = useMemo(() => itemTypeOptionsFor(t), [t]);
  const yearOptions = useMemo(manufacturingYearOptions, []);
  const modeOptions = useMemo(
    () => [
      {
        value: "CREATE_OR_UPDATE",
        label: t.inventoryAddModeCreateOrUpdate,
        hint: t.inventoryAddModeCreateOrUpdateHint,
      },
      {
        value: "CREATE_ONLY",
        label: t.inventoryAddModeCreateOnly,
        hint: t.inventoryAddModeCreateOnlyHint,
      },
      {
        value: "UPDATE_ONLY",
        label: t.inventoryAddModeUpdateOnly,
        hint: t.inventoryAddModeUpdateOnlyHint,
      },
    ],
    [t]
  );

  const goBackToInventory = () => navigate("/inventory-management");

  // Status is set by the backend (never sent), so the saved item's resulting state is
  // echoed back to the operator from body.value.
  const withSavedState = (message, response) => {
    const saved = unwrap(response);
    if (!saved || typeof saved !== "object" || Array.isArray(saved)) return message;
    const parts = [
      saved.status,
      saved.assignedToName,
      saved.storeName ?? saved.storeId,
      saved.location,
    ].filter((part) => part !== null && part !== undefined && part !== "");
    return parts.length ? `${message} (${parts.join(" · ")})` : message;
  };

  // Only sent when a group is picked; the store is always sent alongside it.
  const groupFields = () => (groupId != null ? { assignedTo: Number(groupId) } : {});

  const groupPicker = (testId) => (
    <InventoryGroupPicker
      value={groupName}
      onChange={(name, group) => {
        setGroupName(name);
        setGroupId(group?.groupId ?? null);
      }}
      langMode={langMode}
      open
      testId={testId}
    />
  );

  const updateBulkRow = (index, patch) =>
    setBulkRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  // Prefix / From / To inputs, shown in place of the serial for a SEAL item.
  const sealRangeInputs = (values, onChange, testIdPrefix) =>
    SEAL_RANGE_FIELDS.map((field) => (
      <FormField key={field.key} label={t[field.labelKey]} required={field.required}>
        <TextInput
          value={values[field.key] ?? ""}
          onChange={(value) => onChange(field.key, value)}
          placeholder={t[field.placeholderKey]}
          testId={`${testIdPrefix}-${field.key}`}
        />
      </FormField>
    ));

  const sealPreviewText = (preview) =>
    (t.inventorySealRangePreview || "")
      .replace("{first}", preview.first)
      .replace("{last}", preview.last)
      .replace("{count}", preview.count.toLocaleString());

  const updateBulkMetadata = (index, key, value) =>
    setBulkRows((rows) =>
      rows.map((row, i) =>
        i === index ? { ...row, metadata: { ...row.metadata, [key]: value } } : row
      )
    );

  const saveManual = async () => {
    if (!mode) {
      toast.warn(t.inventoryNoAccessTooltip);
      return;
    }
    const seal = isSeal ? parseSealRange(sealRange) : null;
    if (seal?.error) {
      toast.warn(t[seal.error]);
      return;
    }
    if (!isSeal && !identifier.trim()) {
      toast.warn(t.inventoryAddIdentifierRequired);
      return;
    }
    if (!storeId) {
      toast.warn(t.inventoryAddStoreRequired);
      return;
    }
    // A seal's identifier is built by the backend from the range, so none is sent.
    const sealMetadata = isSeal
      ? Object.fromEntries(SEAL_METADATA_FIELDS.map((key) => [key, metadata[key]]))
      : metadata;
    setSaving(true);
    try {
      const response = await createOrUpdateInventoryItem({
        ...(seal ? seal.range : { identifier: identifier.trim() }),
        itemType,
        mode,
        projectId: projectId ? Number(projectId) : undefined,
        storeId: Number(storeId),
        organizationId,
        // RETURN_TO_STORE only when the operator picked it (the only way to RETURNED);
        // a return needs a destination store, which this form already requires.
        ...(updatesExisting(mode) ? { updateAction } : {}),
        ...groupFields(),
        ...(conditionNote.trim() ? { conditionNote: conditionNote.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        metadata: compactMetadata(sealMetadata),
      });
      toast.success(withSavedState(apiSuccessMessage(response, t.inventoryAddSuccess), response));
      goBackToInventory();
    } catch (error) {
      toast.error(apiErrorMessage(error, t.inventoryAddFailed));
    } finally {
      setSaving(false);
    }
  };

  const saveBulk = async () => {
    if (!mode) {
      toast.warn(t.inventoryNoAccessTooltip);
      return;
    }
    if (!storeId) {
      toast.warn(t.inventoryAddStoreRequired);
      return;
    }
    // A row's own type wins over the shared one; seal rows carry a range, not a serial.
    const rowIsSeal = (row) => isSealType(row.itemType || itemType);
    const filledRows = bulkRows.filter((row) =>
      rowIsSeal(row) ? hasSealInput(row) : row.identifier.trim()
    );
    const invalidSealIndex = filledRows.findIndex(
      (row) => rowIsSeal(row) && parseSealRange(row).error
    );
    if (invalidSealIndex !== -1) {
      const row = filledRows[invalidSealIndex];
      toast.warn(
        `${t.inventoryAddRowLabel} ${bulkRows.indexOf(row) + 1}: ${t[parseSealRange(row).error]}`
      );
      return;
    }
    const items = filledRows.map((row) => {
      const seal = rowIsSeal(row);
      const rowMetadata = seal
        ? Object.fromEntries(SEAL_METADATA_FIELDS.map((key) => [key, row.metadata[key]]))
        : row.metadata;
      return {
        ...(seal ? parseSealRange(row).range : { identifier: row.identifier.trim() }),
        ...(row.itemType ? { itemType: row.itemType } : {}),
        // Same rules as a single save, per item: group id as assignedTo, no status.
        ...groupFields(),
        metadata: compactMetadata(rowMetadata),
      };
    });
    if (items.length === 0) {
      toast.warn(t.inventoryAddItemsRequired);
      return;
    }
    // The backend accepts duplicates and reports them per item, but flagging them
    // here saves a round trip for the common copy/paste mistake. Overlapping seal
    // ranges are checked by the backend.
    const seen = new Set();
    const duplicates = new Set();
    items.forEach((item) => {
      if (!item.identifier) return;
      if (seen.has(item.identifier)) duplicates.add(item.identifier);
      seen.add(item.identifier);
    });
    if (duplicates.size > 0) {
      toast.warn(
        (t.inventoryAddDuplicateIdentifier || "").replace(
          "{values}",
          Array.from(duplicates).join(", ")
        )
      );
    }

    setSaving(true);
    setBulkResult(null);
    try {
      const raw = await bulkCreateOrUpdateInventoryItems({
        projectId: projectId ? Number(projectId) : undefined,
        storeId: Number(storeId),
        organizationId: organizationId ? Number(organizationId) : undefined,
        itemType,
        mode,
        // Request-level default; the backend lets an individual item override it.
        ...(updatesExisting(mode) ? { updateAction } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        items,
      });
      const response = unwrap(raw);
      const failures = firstArray(
        response?.failures,
        response?.errors,
        response?.failedItems,
        response?.rejected
      );
      if (failures.length > 0) {
        // Stay on the screen so the operator can fix the rows that were rejected.
        setBulkResult({ response, failures });
        const partialMessage = apiSuccessMessage(raw, "");
        if (partialMessage) toast.warn(partialMessage);
        return;
      }
      toast.success(apiSuccessMessage(raw, t.inventoryAddBulkSuccess));
      goBackToInventory();
    } catch (error) {
      toast.error(apiErrorMessage(error, t.inventoryAddFailed));
    } finally {
      setSaving(false);
    }
  };

  const storePlaceholder = storesLoading
    ? t.Processing
    : storeOptions.length === 0
      ? t.inventoryImportStoresEmpty
      : t.inventoryImportSelectStorePlaceholder;

  const sharedDestination = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField label={t.inventoryImportSelectStore} required>
        <ReactSelector
          options={storeOptions}
          value={storeOptions.find((option) => option.value === storeId) || null}
          onChange={(option) => setStoreId(option?.value ?? "")}
          placeholder={storePlaceholder}
          isDisabled={storesLoading || storeOptions.length === 0}
          isSearchable
          height="40px"
          borderRadius="8px"
          fontSize="14px"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          dataTestId="inventory-add-store-select"
        />
      </FormField>
      <FormField label={t.inventoryImportItemType} required>
        <ReactSelector
          options={itemTypeOptions}
          value={itemTypeOptions.find((option) => option.value === itemType) || null}
          onChange={(option) => setItemType(option?.value ?? "")}
          placeholder={t.inventoryImportItemTypePlaceholder}
          height="40px"
          borderRadius="8px"
          fontSize="14px"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          dataTestId="inventory-add-item-type-select"
        />
      </FormField>
    </div>
  );

  // Only the modes that touch an existing serial can act on one, so the choice is
  // offered for those and hidden for create-only.
  const updatesExisting = (selectedMode) =>
    selectedMode === "UPDATE_ONLY" || selectedMode === "CREATE_OR_UPDATE";

  const updateActionOptions = [
    {
      value: "UPDATE_DETAILS",
      label: t.inventoryUpdateActionDetails,
      hint: t.inventoryUpdateActionDetailsHint,
    },
    {
      value: "RETURN_TO_STORE",
      label: t.inventoryUpdateActionReturn,
      hint: t.inventoryUpdateActionReturnHint,
    },
  ];
  const selectedUpdateAction =
    updateActionOptions.find((option) => option.value === updateAction) || updateActionOptions[0];

  // Shared by the manual, bulk and Excel upload tabs. The selected option's hint is
  // shown under the dropdown since a select can't carry the description itself.
  const updateActionCards = (
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <FormField label={t.inventoryUpdateActionLabel}>
          <ReactSelector
            options={updateActionOptions}
            value={selectedUpdateAction}
            onChange={(option) => setUpdateAction(option?.value ?? "UPDATE_DETAILS")}
            height="40px"
            borderRadius="8px"
            fontSize="14px"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            dataTestId="inventory-update-action-select"
          />
        </FormField>
        <p className="mt-1.5 text-xs text-slate-400">
          {selectedUpdateAction.hint} {t.inventoryUpdateActionHint}
        </p>
      </div>
    </div>
  );

  const modeCards = (
    <div className="mt-4">
      <p className="text-sm font-medium text-[#374151]">{t.inventoryAddMode}</p>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {modeOptions.map((option) => {
          const allowed = modePermissions[option.value];
          return (
            <OptionCard
              key={option.value}
              name="inventory-add-mode"
              checked={mode === option.value}
              onChange={() => setMode(option.value)}
              label={option.label}
              hint={option.hint}
              disabled={!allowed}
              disabledTooltip={
                option.value === "UPDATE_ONLY"
                  ? t.inventoryNoUpdateAccessTooltip
                  : option.value === "CREATE_ONLY"
                    ? t.inventoryNoCreateAccessTooltip
                    : t.inventoryNoAccessTooltip
              }
              testId={`inventory-add-mode-${option.value.toLowerCase()}`}
            />
          );
        })}
      </div>
      {updatesExisting(mode) && updateActionCards}
    </div>
  );

  const uploadModeCards = (
    <div className="mt-4">
      <p className="text-sm font-medium text-[#374151]">{t.inventoryImportMode}</p>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <OptionCard
          name="inventory-import-mode"
          checked={uploadMode === "CREATE_OR_UPDATE"}
          onChange={() => setUploadMode("CREATE_OR_UPDATE")}
          label={t.inventoryImportModeCreate}
          hint={t.inventoryImportModeCreateHint}
          disabled={!uploadModePermissions.CREATE_OR_UPDATE}
          disabledTooltip={t.inventoryNoCreateAccessTooltip}
          testId="inventory-import-mode-create"
        />
        <OptionCard
          name="inventory-import-mode"
          checked={uploadMode === "UPDATE_ONLY"}
          onChange={() => setUploadMode("UPDATE_ONLY")}
          label={t.inventoryImportModeUpdate}
          hint={t.inventoryImportModeUpdateHint}
          disabled={!uploadModePermissions.UPDATE_ONLY}
          disabledTooltip={t.inventoryNoUpdateAccessTooltip}
          testId="inventory-import-mode-update"
        />
      </div>
      {updatesExisting(uploadMode) && updateActionCards}
    </div>
  );

  // The main (mandatory) columns depend on the item type, so the mapping is built from
  // whichever type is selected when the operator opens it.
  const uploadFields = useMemo(() => uploadFieldsFor(itemType, t), [itemType, t]);
  const itemTypeLabel = itemTypeOptions.find((option) => option.value === itemType)?.label;
  const detectedSerial = detectSerialHeader(itemType, upload.colHeaders);
  const serialField = uploadFields.mandatory.find((field) => field.key === "identifier");
  // Seal sheets have no serial; their From / To columns are matched instead.
  const sealColumnMatch = isSeal ? autoMatchColumns(uploadFields.mandatory, upload.colHeaders || []) : {};
  const sealColumnsFound = uploadFields.mandatory.filter((field) => sealColumnMatch[field.key]);
  const sealColumnsMissing = uploadFields.mandatory.filter((field) => !sealColumnMatch[field.key]);

  // A different sheet has different headers, so its mapping starts over.
  useEffect(() => {
    setColumnMappings({});
  }, [upload.pendingFile]);

  // Checks the destination, then hands over to the column mapping; the import
  // itself starts from the mapping's submit.
  const startUpload = () => {
    if (!upload.pendingFile) {
      toast.warn(t.inventoryImportSelectFileFirst);
      return;
    }
    if (!storeId) {
      toast.warn(t.inventoryImportSelectStoreFirst);
      return;
    }
    if (!itemType) {
      toast.warn(t.inventoryImportItemTypePlaceholder);
      return;
    }
    if (!uploadMode) {
      toast.warn(t.inventoryNoAccessTooltip);
      return;
    }
    setMapOpen(true);
  };

  const submitMapping = async (mappings) => {
    setColumnMappings(mappings);
    setMapOpen(false);
    // No dry run: the sheet is imported in a single pass. itemType is always sent --
    // it is what makes the backend pick the type's serial column.
    const ack = await upload.startRun({
      storeId,
      itemType,
      uploadMode,
      columnMappings: toBackendColumnMappings(mappings, itemType, upload.colHeaders),
      ...(updatesExisting(uploadMode) ? { updateAction } : {}),
    });
    // Once the backend has accepted the sheet the run shows up in the history (as
    // in progress), so that is where the operator follows it, and the upload form
    // starts clean for the next sheet -- the history keeps refreshing itself until
    // the run finishes. A rejected sheet stays here with its error so the mapping
    // can be fixed and resubmitted.
    if (ack) {
      upload.resetAll();
      setColumnMappings({});
      setImportView("history");
    }
  };

  const uploadSummary = upload.summary;

  const themeClass =
    theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "";

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className={`flex h-full min-h-0 w-full flex-col overflow-hidden bg-slate-50 ${themeClass}`}
      data-testid="inventory-add-screen"
    >
      <div className="mx-3 mt-3 shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:mx-4 sm:px-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={goBackToInventory}
            aria-label={t.gobacklabel}
            data-testid="inventory-add-back-to-list"
            className="flex h-9 w-9 min-h-[36px] items-center justify-center rounded-lg bg-blue-50 text-[#236DB4] transition duration-200 ease-in-out hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-[#236DB4]/30"
          >
            {isRtl ? <HiOutlineArrowLongRight size={20} /> : <HiOutlineArrowLongLeft size={20} />}
          </button>
          <div>
            <h1 className="text-lg font-bold leading-tight text-slate-800">{t.inventoryAddTitle}</h1>
            <p className="mt-0.5 text-xs text-slate-400">{t.inventoryAddSubtitle}</p>
          </div>
        </div>

        <div className="mt-4 inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setEntryMode("manual")}
            data-testid="inventory-add-tab-manual"
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
              entryMode === "manual"
                ? "bg-white text-[#236DB4] shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Plus size={14} /> {t.inventoryAddTabManual}
          </button>
          <button
            type="button"
            onClick={() => setEntryMode("bulk")}
            data-testid="inventory-add-tab-bulk"
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
              entryMode === "bulk"
                ? "bg-white text-[#236DB4] shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <ListChecks size={14} /> {t.inventoryAddTabBulk}
          </button>
          <button
            type="button"
            onClick={() => setEntryMode("import")}
            data-testid="inventory-add-tab-import"
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
              entryMode === "import"
                ? "bg-white text-[#236DB4] shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <FileSpreadsheet size={14} /> {t.inventoryAddTabImport}
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
        <div className="w-full space-y-4">
          {entryMode === "manual" ? (
            <>
              <SectionCard index={1} title={t.inventoryAddSectionDestination}>
                {sharedDestination}
                {modeCards}
              </SectionCard>

              <SectionCard index={2} title={t.inventoryAddSectionDetails}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {isSeal ? (
                    sealRangeInputs(
                      sealRange,
                      (key, value) => setSealRange((prev) => ({ ...prev, [key]: value })),
                      "inventory-add-seal"
                    )
                  ) : (
                    <FormField label={t.inventoryMeterNumber} required>
                      <TextInput
                        value={identifier}
                        onChange={setIdentifier}
                        placeholder={t.inventoryAddIdentifierPlaceholder}
                        testId="inventory-add-identifier-input"
                      />
                    </FormField>
                  )}
                  <FormField label={t.inventoryAddGroupAssignment}>
                    {groupPicker("inventory-add-group-picker")}
                  </FormField>
                  <FormField label={t.inventoryAddConditionNote}>
                    <TextInput
                      value={conditionNote}
                      onChange={setConditionNote}
                      testId="inventory-add-condition-input"
                    />
                  </FormField>
                  <FormField label={t.inventoryAddNote} className="sm:col-span-2">
                    <TextInput value={note} onChange={setNote} testId="inventory-add-note-input" />
                  </FormField>
                </div>
                {sealPreview && (
                  <p
                    className={`mt-3 text-xs ${sealPreview.error ? "text-red-600" : "text-slate-500"}`}
                    data-testid="inventory-add-seal-preview"
                  >
                    {sealPreview.error ? t[sealPreview.error] : sealPreviewText(sealPreview)}
                  </p>
                )}
              </SectionCard>

              <SectionCard index={3} title={t.inventoryAddSectionMetadata}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
                  {METADATA_FIELDS.filter(
                    (field) => !isSeal || SEAL_METADATA_FIELDS.includes(field.key)
                  ).map((field) => (
                    <FormField key={field.key} label={t[field.labelKey]}>
                      {field.key === "manufacturingYear" ? (
                        <YearSelect
                          value={metadata[field.key] ?? ""}
                          onChange={(value) =>
                            setMetadata((prev) => ({ ...prev, [field.key]: value }))
                          }
                          options={yearOptions}
                          placeholder={t.inventoryAddSelectYear}
                          testId={`inventory-add-metadata-${field.key}`}
                        />
                      ) : (
                        <TextInput
                          value={metadata[field.key] ?? ""}
                          onChange={(value) =>
                            setMetadata((prev) => ({ ...prev, [field.key]: value }))
                          }
                          testId={`inventory-add-metadata-${field.key}`}
                        />
                      )}
                    </FormField>
                  ))}
                </div>
              </SectionCard>
            </>
          ) : entryMode === "bulk" ? (
            <>
              <SectionCard index={1} title={t.inventoryAddSectionDestination}>
                {sharedDestination}
                {modeCards}
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField label={t.inventoryAddGroupAssignment}>
                    {groupPicker("inventory-add-bulk-group-picker")}
                  </FormField>
                  <FormField label={t.inventoryAddNote}>
                    <TextInput
                      value={note}
                      onChange={setNote}
                      testId="inventory-add-bulk-note-input"
                    />
                  </FormField>
                </div>
              </SectionCard>

              <SectionCard
                index={2}
                title={t.inventoryAddSectionItems}
                subtitle={t.inventoryAddBulkHint}
              >
                <div className="space-y-3">
                  {bulkRows.map((row, index) => {
                    const sealRow = isSealType(row.itemType || itemType);
                    const rowSealPreview = sealRow && hasSealInput(row) ? parseSealRange(row) : null;
                    return (
                    <div
                      key={index}
                      className="rounded-lg border border-slate-200 bg-slate-50/70 p-3"
                      data-testid="inventory-add-bulk-row"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          {t.inventoryAddRowLabel} {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setBulkRows((rows) =>
                              rows.length === 1
                                ? [emptyBulkRow()]
                                : rows.filter((_, i) => i !== index)
                            )
                          }
                          aria-label={t.inventoryAddRemoveRow}
                          className="rounded-md p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                          data-testid="inventory-add-bulk-remove-row"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
                        {sealRow ? (
                          sealRangeInputs(
                            row,
                            (key, value) => updateBulkRow(index, { [key]: value }),
                            "inventory-add-bulk-seal"
                          )
                        ) : (
                          <FormField label={t.inventoryMeterNumber} required>
                            <TextInput
                              value={row.identifier}
                              onChange={(value) => updateBulkRow(index, { identifier: value })}
                              placeholder={t.inventoryAddIdentifierPlaceholder}
                              testId="inventory-add-bulk-identifier"
                            />
                          </FormField>
                        )}
                        <FormField label={t.inventoryImportItemType}>
                          <ReactSelector
                            options={itemTypeOptions}
                            value={
                              itemTypeOptions.find((option) => option.value === row.itemType) || null
                            }
                            onChange={(option) =>
                              updateBulkRow(index, { itemType: option?.value ?? "" })
                            }
                            placeholder={t.inventoryImportItemTypePlaceholder}
                            isClearable
                            height="40px"
                            borderRadius="8px"
                            fontSize="14px"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            dataTestId="inventory-add-bulk-item-type"
                          />
                        </FormField>
                        {BULK_METADATA_FIELDS.filter(
                          (key) => !sealRow || SEAL_METADATA_FIELDS.includes(key)
                        ).map((key) => {
                          const field = METADATA_FIELDS.find((item) => item.key === key);
                          return (
                            <FormField key={key} label={t[field.labelKey]}>
                              {key === "manufacturingYear" ? (
                                <YearSelect
                                  value={row.metadata[key] ?? ""}
                                  onChange={(value) => updateBulkMetadata(index, key, value)}
                                  options={yearOptions}
                                  placeholder={t.inventoryAddSelectYear}
                                  testId={`inventory-add-bulk-${key}`}
                                />
                              ) : (
                                <TextInput
                                  value={row.metadata[key] ?? ""}
                                  onChange={(value) => updateBulkMetadata(index, key, value)}
                                  testId={`inventory-add-bulk-${key}`}
                                />
                              )}
                            </FormField>
                          );
                        })}
                      </div>
                      {rowSealPreview && (
                        <p
                          className={`mt-2 text-xs ${
                            rowSealPreview.error ? "text-red-600" : "text-slate-500"
                          }`}
                        >
                          {rowSealPreview.error
                            ? t[rowSealPreview.error]
                            : sealPreviewText(rowSealPreview)}
                        </p>
                      )}
                    </div>
                    );
                  })}
                </div>

                <button data-button-intent="primary"
                  type="button"
                  onClick={() => setBulkRows((rows) => [...rows, emptyBulkRow()])}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg border border-dashed border-[#93C5FD] px-4 py-2 text-sm font-semibold text-[#236DB4] transition hover:bg-blue-50"
                  data-testid="inventory-add-bulk-add-row"
                >
                  <Plus size={15} /> {t.inventoryAddAddRow}
                </button>
              </SectionCard>

              {bulkResult && (
                <SectionCard index={3} title={t.inventoryAddResultsTitle}>
                  <ul className="space-y-2">
                    {bulkResult.failures.map((failure, index) => (
                      <li
                        key={`${failure?.identifier ?? "row"}-${index}`}
                        className="rounded-lg border border-red-100 bg-red-50/60 px-3 py-2 text-xs text-red-700"
                      >
                        <span className="font-semibold">
                          {failure?.identifier ?? `${t.inventoryAddRowLabel} ${index + 1}`}:{" "}
                        </span>
                        <span>
                          {typeof failure === "string"
                            ? failure
                            : failure?.message ?? failure?.error ?? failure?.reason ?? ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              )}
            </>
          ) : (
            <>
              <div
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1"
                role="tablist"
              >
                {[
                  { value: "upload", label: t.inventoryImportTabNew, Icon: Upload },
                  { value: "history", label: t.inventoryImportTabHistory, Icon: History },
                ].map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={importView === value}
                    onClick={() => setImportView(value)}
                    data-testid={`inventory-import-view-${value}`}
                    className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                      importView === value
                        ? "bg-[#236DB4] text-white shadow-sm"
                        : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                    }`}
                  >
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>

              {importView === "history" ? (
                <InventoryUploadHistory refreshKey={historyRefreshKey} />
              ) : (
              <>
              <SectionCard index={1} title={t.inventoryAddSectionDestination}>
                {sharedDestination}
                {uploadModeCards}
              </SectionCard>

              <SectionCard
                index={2}
                title={t.inventoryImportStepUpload}
                subtitle={t.inventoryImportUploadDesc}
              >
                {isSeal && (
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2.5 text-xs text-slate-600">
                    <span>{t.inventoryImportSealColumns}</span>
                    <button
                      type="button"
                      onClick={downloadSealTemplate}
                      className="inline-flex items-center gap-1.5 font-semibold text-[#236DB4] hover:underline"
                      data-testid="inventory-import-seal-template"
                    >
                      <Download size={13} /> {t.inventoryImportDownloadSealTemplate}
                    </button>
                  </div>
                )}
                <UploadDropZone upload={upload} t={t} />
                {upload.pendingFile && isSeal && (
                  <div
                    className={`mt-3 rounded-lg border px-3 py-2.5 text-xs ${
                      sealColumnsMissing.length === 0
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    }`}
                    data-testid="inventory-import-seal-columns"
                  >
                    <span className="font-semibold">
                      {t.inventoryImportMainColumns} ({itemTypeLabel}):{" "}
                      {uploadFields.mandatory.map((field) => field.label).join(", ")}
                    </span>
                    {sealColumnsFound.length > 0 && (
                      <span className="mt-1 block">
                        {(t.inventoryImportSealColumnsFound || "").replace(
                          "{found}",
                          sealColumnsFound
                            .map((field) => `${field.label} ← ${sealColumnMatch[field.key]}`)
                            .join(", ")
                        )}
                      </span>
                    )}
                    {sealColumnsMissing.length > 0 && (
                      <span className="mt-1 block">
                        {(t.inventoryImportSealColumnsMissing || "").replace(
                          "{missing}",
                          sealColumnsMissing.map((field) => field.label).join(", ")
                        )}
                      </span>
                    )}
                  </div>
                )}
                {upload.pendingFile && serialField && (
                  <div
                    className={`mt-3 rounded-lg border px-3 py-2.5 text-xs ${
                      detectedSerial
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    }`}
                    data-testid="inventory-import-main-column"
                  >
                    <span className="font-semibold">
                      {t.inventoryImportMainColumns} ({itemTypeLabel}): {serialField.label}
                    </span>
                    <span className="mt-1 block">
                      {detectedSerial
                        ? (t.inventoryImportSerialDetected || "").replace("{header}", detectedSerial)
                        : t.inventoryImportSerialNotFound}
                    </span>
                    <span className="mt-1 block opacity-70">
                      {(t.inventoryImportSerialAccepted || "").replace(
                        "{names}",
                        serialField.aliases.join(", ")
                      )}
                    </span>
                  </div>
                )}
              </SectionCard>

              {(upload.submitting || upload.runError || uploadSummary) && (
                <SectionCard index={3} title={t.inventoryAddResultsTitle}>
                  <div className="space-y-4">
                    {upload.submitting && (
                      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <p className="text-xs font-medium text-slate-600">
                          {t.inventoryImportUploading}
                        </p>
                        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100">
                          <div
                            className="h-1.5 rounded-full bg-[#236DB4] transition-all"
                            style={{ width: `${upload.uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {upload.runError && (
                      <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-600" />
                        <div>
                          <p className="text-sm font-semibold text-red-700">
                            {t.inventoryImportFailed}
                          </p>
                          <p className="mt-0.5 text-xs text-red-600">{upload.runError}</p>
                        </div>
                      </div>
                    )}

                    {uploadSummary && !upload.runError && (
                      <>
                        <div
                          className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
                            upload.runFailed
                              ? "border-red-200 bg-red-50"
                              : !upload.isTerminal
                                ? "border-blue-200 bg-blue-50"
                                : upload.hasIssues
                                  ? "border-amber-200 bg-amber-50"
                                  : "border-emerald-200 bg-emerald-50"
                          }`}
                        >
                          {!upload.isTerminal ? (
                            <span className="mt-0.5 shrink-0">
                              <InlineSpinner size={18} />
                            </span>
                          ) : upload.runFailed || upload.hasIssues ? (
                            <AlertTriangle
                              size={18}
                              className={`mt-0.5 shrink-0 ${
                                upload.runFailed ? "text-red-600" : "text-amber-600"
                              }`}
                            />
                          ) : (
                            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-slate-800">
                                {!upload.isTerminal
                                  ? t.inventoryImportRunning
                                  : upload.runFailed
                                    ? t.inventoryImportFailed
                                    : t.inventoryImportSuccess}
                              </p>
                              <UploadStatusBadge status={uploadSummary.status} t={t} />
                              {upload.runId && (
                                <span className="text-xs text-slate-400">
                                  {t.inventoryImportUploadIdLabel} #{upload.runId}
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 text-xs text-slate-600">
                              {uploadSummary.failureReason || uploadSummary.message || ""}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                          {uploadSummary.total !== null && (
                            <SummaryTile
                              label={t.inventoryImportTotalRows}
                              value={uploadSummary.total}
                            />
                          )}
                          {uploadSummary.created !== null && (
                            <SummaryTile
                              label={t.inventoryImportCreatedRows}
                              value={uploadSummary.created}
                              tone="green"
                            />
                          )}
                          {uploadSummary.updated !== null && (
                            <SummaryTile
                              label={t.inventoryImportUpdatedRows}
                              value={uploadSummary.updated}
                              tone="blue"
                            />
                          )}
                          {uploadSummary.skipped !== null && (
                            <SummaryTile
                              label={t.inventoryImportSkippedRows}
                              value={uploadSummary.skipped}
                            />
                          )}
                          {uploadSummary.failed !== null && (
                            <SummaryTile
                              label={t.inventoryImportFailedRows}
                              value={uploadSummary.failed}
                              tone={uploadSummary.failed > 0 ? "red" : "slate"}
                            />
                          )}
                          {uploadSummary.notUploadedCount !== null && (
                            <SummaryTile
                              label={t.inventoryImportNotUploadedCount}
                              value={uploadSummary.notUploadedCount}
                              tone={uploadSummary.notUploadedCount > 0 ? "red" : "slate"}
                            />
                          )}
                        </div>

                        {upload.visibleRejects.length > 0 && (
                          <div>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                {t.inventoryImportRejectedTitle}
                              </p>
                              <div className="flex items-center gap-4">
                                <button
                                  type="button"
                                  onClick={upload.downloadRejects}
                                  disabled={upload.rejectsExporting}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#236DB4] hover:underline disabled:opacity-50"
                                  data-testid="inventory-import-download-not-uploaded"
                                >
                                  {upload.rejectsExporting ? (
                                    <InlineSpinner size={13} />
                                  ) : (
                                    <FileSpreadsheet size={13} />
                                  )}
                                  {t.inventoryImportDownloadNotUploaded}
                                </button>
                                {uploadSummary.failureLogUrl && (
                                  <a
                                    href={uploadSummary.failureLogUrl}
                                    download
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#236DB4] hover:underline"
                                    data-testid="inventory-import-failure-log"
                                  >
                                    <Download size={13} />
                                    {t.inventoryImportDownloadFailureLog}
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="mt-2">
                              <RejectedRowList rows={upload.visibleRejects} t={t} />
                            </div>
                            {!upload.extraRejects &&
                              (uploadSummary.notUploadedTruncated ||
                                uploadSummary.notUploaded.length > MAX_VISIBLE_ROW_ERRORS) && (
                                <p className="mt-2 text-xs text-slate-400">
                                  {(t.inventoryImportRejectedTruncated || "").replace(
                                    "{count}",
                                    Math.min(
                                      MAX_VISIBLE_ROW_ERRORS,
                                      uploadSummary.notUploaded.length
                                    )
                                  )}
                                </p>
                              )}
                            {upload.runId && !upload.rejectsExhausted && (
                              <button
                                type="button"
                                onClick={upload.loadMoreRejects}
                                disabled={upload.rejectsLoading}
                                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                                data-testid="inventory-import-load-rejects"
                              >
                                {upload.rejectsLoading && <InlineSpinner size={12} />}
                                {t.inventoryImportLoadAllRejected}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </SectionCard>
              )}
              </>
              )}
            </>
          )}
        </div>
      </div>

      <InventoryColumnMapModal
        open={mapOpen}
        fields={uploadFields}
        headers={upload.colHeaders}
        initialMappings={columnMappings}
        itemType={itemType}
        itemTypeLabel={itemTypeLabel}
        fileName={upload.pendingFile?.name}
        onBack={(mappings) => {
          // Back to the Upload File step keeps what has been mapped so far.
          setColumnMappings(mappings);
          setMapOpen(false);
        }}
        onClose={() => setMapOpen(false)}
        onSubmit={submitMapping}
      />

      <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#E5E7EB] bg-white px-4 py-4 sm:px-6">
        <CommonButton colorIntent="danger"
          onClick={goBackToInventory}
          variant="outline"
          theme={theme}
          disabled={saving || upload.isRunning}
          className={FOOTER_BUTTON_CLASS}
          data-testid="inventory-add-cancel-button"
        >
          {t.cancel}
        </CommonButton>
        {entryMode === "import" ? (
          // The history view is read-only, so it has no primary action. The sheet is
          // already written by the time a run finishes, so the action then turns into
          // a way out of the screen rather than a second submit.
          importView === "history" ? null : upload.importDone ? (
            <CommonButton colorIntent="danger"
              onClick={goBackToInventory}
              variant="primary"
              theme={theme}
              className={FOOTER_BUTTON_CLASS}
              data-testid="inventory-import-done-button"
            >
              {t.close ?? t.cancel}
            </CommonButton>
          ) : (
            <CommonButton colorIntent="success"
              onClick={startUpload}
              variant="primary"
              theme={theme}
              disabled={upload.isRunning || upload.readingHeaders || !uploadMode}
              className={FOOTER_BUTTON_CLASS}
              data-testid="inventory-import-confirm-button"
            >
              {upload.isRunning ? t.inventoryImportUploading : t.inventoryImportMapAndImport}
            </CommonButton>
          )
        ) : (
          <CommonButton colorIntent="success"
            onClick={entryMode === "manual" ? saveManual : saveBulk}
            variant="primary"
            theme={theme}
            disabled={saving || !mode}
            className={FOOTER_BUTTON_CLASS}
            data-testid="inventory-add-save-button"
          >
            {saving ? t.inventoryAddSaving : t.inventoryAddSave}
          </CommonButton>
        )}
      </div>
    </div>
  );
};

export default AddInventory;
